/**
 * Partner discount: attribution, creator loading, and DOM apply logic.
 */
import { getSessionItem, setSessionItem } from '@/helpers/session.storage'
import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import type { PartnerCreator } from '@/models/interfaces'
import { backendPublic } from '@/services/backend.public'

const MONTHLY_PRICE = 2400
const ANNUAL_PRICE = 19700

/**
 * Aggregate % vs monthly annualized after annual promo face %.
 * round((monthlyYear - discountedAnnual) / monthlyYear * 100)
 */
export function aggregatePartnerDiscountPercent(facePercent: number): number {
  const monthlyYear = MONTHLY_PRICE * 12
  const discountedAnnual = ANNUAL_PRICE * (1 - facePercent / 100)
  return Math.round(((monthlyYear - discountedAnnual) / monthlyYear) * 100)
}

function getPartnerSourceFromUrl(url: string | null): string | null {
  if (!url) {
    return null
  }
  try {
    const searchParams = new URL(url).searchParams
    const isPartnerMarketingUrl = searchParams.get('utm_medium') === 'partner'
    const utmSource = searchParams.get('utm_source')
    if (!isPartnerMarketingUrl || !utmSource) {
      return null
    }
    return utmSource
  } catch {
    return null
  }
}

/**
 * Whether the visitor has partner attribution in the URL.
 */
export function hasPartnerAttribution(): boolean {
  return !!getPartnerSource()
}

function getPartnerSource(): string | null {
  const query = window.location.search
  if (query) {
    const source = getPartnerSourceFromUrl(window.location.href)
    if (source) {
      return source
    }
    return null
  }

  const marketingUrl = storage.getItem(StorageKeys.marketingUrl)
  if (marketingUrl) {
    const source = getPartnerSourceFromUrl(marketingUrl)
    if (source) {
      return source
    }
  }

  const firstUrl = storage.getItem(StorageKeys.firstPage)
  if (firstUrl) {
    const source = getPartnerSourceFromUrl(firstUrl)
    if (source) {
      return source
    }
  }

  return null
}

/**
 * Load partner creator from the backend into session storage.
 * Call once on page init; use getLocalCreator() for sync reads afterward.
 */
export async function loadCreator(): Promise<void> {
  const source = getPartnerSource()
  if (!source) {
    return
  }

  if (getLocalCreator()) {
    return
  }

  try {
    const fromBackend = await backendPublic.getPartnerCreator(source)
    setSessionItem(StorageKeys.partnerCreatorSession, fromBackend)
  } catch (error) {
    if (window.__UNIT_TESTING !== true) {
      try {
        window.Sentry?.captureException(error)
      } catch {
        // Ignore errors from Sentry itself.
      }
    }
  }
}

/**
 * Get partner creator from session storage.
 */
export function getLocalCreator(): PartnerCreator | null {
  const source = getPartnerSource()
  if (!source) {
    return null
  }
  const cached = getSessionItem<PartnerCreator>(
    StorageKeys.partnerCreatorSession,
  )
  if (cached?.utm_source === source) {
    return cached
  }
  return null
}

/**
 * Resolved discount prompt with {discount} replaced by aggregate %.
 */
export function resolvedDiscountPrompt(creator: PartnerCreator): string {
  const aggregate = aggregatePartnerDiscountPercent(creator.value_annual)
  return creator.discount_prompt.replace(/{discount}/g, aggregate.toString())
}

/**
 * Short label for discount (e.g. "49%") for use in headings.
 */
export function discountOffLabel(creator: PartnerCreator): string {
  return `${aggregatePartnerDiscountPercent(creator.value_annual)}%`
}

let popupDelayTimeoutId: ReturnType<typeof setTimeout> | null = null
const dismissed = {
  banner: false,
  popup: false,
}

function isDismissed(type: 'banner' | 'popup'): boolean {
  return dismissed[type]
}

/**
 * Clear in-memory dismiss flags and any pending popup timer.
 */
export function resetPartnerDiscountDismissState(): void {
  dismissed.banner = false
  dismissed.popup = false
  if (popupDelayTimeoutId !== null) {
    clearTimeout(popupDelayTimeoutId)
    popupDelayTimeoutId = null
  }
}

export function updatePartnerDiscountBody(): void {
  document.body.classList.add('has-partner-discount')

  if (!isDismissed('banner')) {
    document.body.classList.add('has-partner-discount-banner')
    const navbar = document.querySelector(
      '.partner-discount-banner',
    )?.parentElement
    if (navbar) {
      navbar.classList.add('navbar--with-partner-discount')
    }
  }

  if (isDismissed('popup')) {
    if (popupDelayTimeoutId !== null) {
      clearTimeout(popupDelayTimeoutId)
      popupDelayTimeoutId = null
    }
    return
  }

  if (popupDelayTimeoutId !== null) {
    return
  }
  popupDelayTimeoutId = setTimeout(() => {
    popupDelayTimeoutId = null
    if (document.body && !isDismissed('popup')) {
      document.body.classList.add('has-partner-discount-popup')
    }
  }, 1000)
}

function replaceBannerText(
  textContainer: Element,
  fullPrompt: string,
  percentLabel?: string,
): void {
  textContainer.textContent = ''
  const specialStrong = document.createElement('strong')
  specialStrong.textContent = 'Special Deal: '
  textContainer.appendChild(specialStrong)

  if (!percentLabel) {
    textContainer.appendChild(document.createTextNode(fullPrompt))
    return
  }

  const highlightStartIndex = fullPrompt.indexOf(percentLabel)

  if (highlightStartIndex === -1) {
    textContainer.appendChild(document.createTextNode(fullPrompt))
    return
  }

  textContainer.appendChild(
    document.createTextNode(fullPrompt.slice(0, highlightStartIndex)),
  )
  const strong = document.createElement('strong')
  strong.className = 'partner-discount-banner__headline'
  strong.textContent = percentLabel
  textContainer.appendChild(strong)
  textContainer.appendChild(
    document.createTextNode(
      fullPrompt.slice(highlightStartIndex + percentLabel.length),
    ),
  )
}

function setPercentage(valueWithPercent: string): void {
  const couponValue = document.querySelector(
    '.partner-discount-coupon .partner-discount-coupon__value',
  )
  if (couponValue) {
    couponValue.textContent = valueWithPercent
  }
  const popupHighlight = document.querySelector(
    '.partner-discount-popup .partner-discount-popup__highlight',
  )
  if (popupHighlight) {
    popupHighlight.textContent = `${valueWithPercent} `
  }
}

export function applyPartnerDiscountText(creator: PartnerCreator): void {
  const fullPrompt = resolvedDiscountPrompt(creator)
  const aggregatePercent = aggregatePartnerDiscountPercent(creator.value_annual)

  setPercentage(`${aggregatePercent}%`)

  const textContainer = document.querySelector('.partner-discount-banner__text')
  if (textContainer) {
    replaceBannerText(textContainer, fullPrompt, `${aggregatePercent}% off`)
  }

  const popupText = document.querySelector('.partner-discount-popup__text')
  if (popupText) {
    popupText.textContent = fullPrompt
  }
}

export function applyPricingContent(creator: PartnerCreator): void {
  const couponJoinText = document.querySelector(
    '.partner-discount-coupon__text--join',
  )
  if (couponJoinText) {
    couponJoinText.textContent = resolvedDiscountPrompt(creator)
  }

  const baseAnnualPerMonth = 1642
  const discountMultiplier = 1 - creator.value_annual / 100

  const reducedPerMonthCents = Math.round(
    baseAnnualPerMonth * discountMultiplier,
  )
  const annualPrice = `$${(reducedPerMonthCents / 100).toFixed(2)}/mo`
  const totalAnnualCents = Math.round(
    baseAnnualPerMonth * 12 * discountMultiplier,
  )

  const defaultAnnualPrice = 197

  const creatorBilledAnnualNew = `$${(totalAnnualCents / 100).toFixed(2)}`
  const creatorBilledAnnualOld = `$${defaultAnnualPrice.toFixed(0)}`
  const annualPriceOriginalMo = `$${(defaultAnnualPrice / 12).toFixed(2)}/month`

  const content = document.querySelector('.pricing__content')
  if (content) {
    content.classList.add('pricing__content--variant-b')
  }

  applyHomepageAnnualPrice(
    annualPrice,
    annualPriceOriginalMo,
    creatorBilledAnnualNew,
    creatorBilledAnnualOld,
  )

  const card = document.querySelector('.pricing .p-card--active')
  if (!card) {
    return
  }

  const header = card.querySelector('.p-card__header')
  if (header) {
    const existingBadge = card.querySelector('.p-card__badge--creator')
    if (!existingBadge) {
      const badge = document.createElement('div')
      badge.className = 'p-card__badge p-card__badge--creator'
      badge.textContent = `Claim Discount • Save ${aggregatePartnerDiscountPercent(creator.value_annual)}%`
      header.parentNode?.insertBefore(badge, header)
    }
  }

  const priceEl = card.querySelector('.p-card__price')
  if (priceEl) {
    priceEl.textContent = annualPrice
    priceEl.classList.add('p-card__price--creator')
    const wrap = priceEl.closest('.p-card__price-wrap')
    if (wrap) {
      let oldMo = wrap.querySelector('.p-card__price-old-mo')
      if (!oldMo) {
        oldMo = document.createElement('span')
        oldMo.className = 'p-card__price-old-mo'
        wrap.appendChild(oldMo)
      }
      oldMo.textContent = annualPriceOriginalMo
    }
  }

  const billedEl = card.querySelector('.p-card__billed-monthly')
  if (billedEl) {
    billedEl.innerHTML = `Billed annually at ${creatorBilledAnnualNew} • <span class="p-card__billed-old">${creatorBilledAnnualOld}</span>`
  }

  const monthlyCard = document.querySelector(
    '.pricing .p-card:not(.p-card--active)',
  )
  if (monthlyCard) {
    const monthlyPriceEl = monthlyCard.querySelector('.p-card__price')
    if (monthlyPriceEl) {
      let wrap = monthlyPriceEl.closest('.p-card__price-wrap')
      if (!wrap) {
        wrap = document.createElement('div')
        wrap.className = 'p-card__price-wrap p-card__price-wrap--align'
        const parent = monthlyPriceEl.parentNode
        if (parent) {
          parent.insertBefore(wrap, monthlyPriceEl)
          wrap.appendChild(monthlyPriceEl)
        }
      } else {
        wrap.classList.add('p-card__price-wrap--align')
      }
    }
  }
}

/**
 * Homepage cards don't use the pricing page's `.p-card` markup.
 * Write the same annual figures onto the homepage annual card.
 */
function applyHomepageAnnualPrice(
  annualPrice: string,
  annualPriceOriginalMo: string,
  billedAnnualNew: string,
  billedAnnualOld: string,
): void {
  const card = document.querySelector('.index__pricing .pricing__card--primary')
  if (!card) {
    return
  }

  const priceEl = card.querySelector('.pricing__price')
  if (priceEl && !priceEl.querySelector('.p-card__price-old-mo')) {
    priceEl.textContent = annualPrice
    const oldMo = document.createElement('span')
    oldMo.className = 'p-card__price-old-mo'
    oldMo.textContent = annualPriceOriginalMo
    priceEl.append(oldMo)
  }

  const subtitle = card.querySelector('.pricing__subtitle')
  if (subtitle && !subtitle.querySelector('.p-card__billed-old')) {
    subtitle.innerHTML = `Billed annually at <strong>${billedAnnualNew}</strong> • <span class="p-card__billed-old">${billedAnnualOld}</span>`
  }
}

export function initPartnerDiscountDismissHandlers(): void {
  document.body.addEventListener('click', (e) => {
    const target = e.target as Element
    const bannerDismiss = target.closest(
      '.partner-discount-banner__dismiss, [aria-label="Dismiss banner"]',
    )
    if (bannerDismiss) {
      dismissed.banner = true
      document.body.classList.remove('has-partner-discount-banner')
      const navbar = document.querySelector(
        '.partner-discount-banner',
      )?.parentElement
      if (navbar) {
        navbar.classList.remove('navbar--with-partner-discount')
      }
      updatePartnerDiscountBody()
      return
    }

    const popupDismiss = target.closest(
      '.partner-discount-popup__close, .partner-discount-popup__backdrop',
    )
    if (popupDismiss) {
      dismissed.popup = true
      document.body.classList.remove('has-partner-discount-popup')

      updatePartnerDiscountBody()
    }
  })
}
